import React, { useState, useEffect, useCallback, useRef } from 'react';
import { TOAST_EVENT } from '../actions/toastBus';
import { LuCircleCheck, LuCircleX, LuInfo, LuX } from 'react-icons/lu';
import '../stylesheet/ToastHost.css';

const ICONS = { success: LuCircleCheck, error: LuCircleX, info: LuInfo };
const AUTO_DISMISS_MS = 4200;
const EXIT_MS = 320;

let nextId = 1;

// Mounted once in App.jsx (like DarkModeToggle) so it survives every page
// navigation - any component anywhere can fire showToast(message, type)
// from actions/toastBus and have it show up here, replacing the native
// browser alert() popups that used to interrupt every add/edit/delete.
export default function ToastHost() {
    const [toasts, setToasts] = useState([]);
    const timers = useRef({});

    const removeToast = useCallback((id) => {
        setToasts((prev) => prev.filter((t) => t.id !== id));
        window.clearTimeout(timers.current[id]);
        delete timers.current[id];
    }, []);

    const dismissToast = useCallback((id) => {
        setToasts((prev) => prev.map((t) => (t.id === id ? { ...t, closing: true } : t)));
        window.clearTimeout(timers.current[id]);
        timers.current[id] = window.setTimeout(() => removeToast(id), EXIT_MS);
    }, [removeToast]);

    useEffect(() => {
        const handleToast = (event) => {
            const { message, type } = event.detail || {};
            if (!message) return;
            const id = nextId++;
            setToasts((prev) => [...prev, { id, message, type: type || 'success', closing: false }]);
            timers.current[id] = window.setTimeout(() => dismissToast(id), AUTO_DISMISS_MS);
        };
        window.addEventListener(TOAST_EVENT, handleToast);
        return () => {
            window.removeEventListener(TOAST_EVENT, handleToast);
            Object.values(timers.current).forEach((t) => window.clearTimeout(t));
        };
    }, [dismissToast]);

    if (toasts.length === 0) return null;

    return (
        <div className="toast-host" role="status" aria-live="polite">
            {toasts.map(({ id, message, type, closing }) => {
                const Icon = ICONS[type] || LuCircleCheck;
                return (
                    <div key={id} className={`toast-card toast-${type}${closing ? ' is-closing' : ''}`}>
                        <span className="toast-icon"><Icon /></span>
                        <span className="toast-message">{message}</span>
                        <button
                            type="button"
                            className="toast-close"
                            onClick={() => dismissToast(id)}
                            aria-label="Dismiss notification"
                        >
                            <LuX />
                        </button>
                    </div>
                );
            })}
        </div>
    );
}
